import { xIsNull, xIsUndefined, xIsArray, xIsObject, xIsSpecificClass, xIsObjectEmpty, xIsString, xIsSet, xIsFunction, xIsAnySpecificClass, xIsInteger } from './XTypeChecks.js';
import { XObject, XArray, XString, XNumber, XInteger, XBoolean, XReference, XAttachmentHandler, XPrimitive, xExtendClassForAttachments, xComputed, xComputedOutput } from './XTypes.js';
import { xChangeElementType, xRandomString } from './XUtil.js';
import { XTypeError } from './XError.js';

function xTemplateToElements(template)
{
    const dom = new DOMParser().parseFromString(template, 'text/html');
    if('xKeep' in dom.documentElement.dataset)
    {
        delete dom.documentElement.dataset['xKeep'];
        return dom;
    }
    if('xKeep' in dom.body.dataset)
    {
        delete dom.body.dataset['xKeep'];
        dom.body.remove();
        return dom.body;
    }
    const elements = Array.from(dom.body.children);
    for(const element of elements)
    {
        element.remove();
    }
    return elements;
}

export class XGenerativeDOM
{
    #parent;
    #element;
    #data;
    #attachment = {
        reference: this,
        _destroy: this._destroy.bind(this)
    };

    constructor(data, parent)
    {
        this.#parent = parent;
        this.#data = data;
        this.#data._addAttachment(this.#attachment);
        if(this.#data instanceof XObject)
        {
            this.children = {};
            this.#element = document.createElement('ul');
            for(const key in this.#data._value)
            {
                const elementItem = document.createElement('li');
                const elementKey = document.createElement('b');
                const child = new XGenerativeDOM(this.#data._value[key], this);
                elementKey.innerText = key + ': ';
                elementItem.appendChild(elementKey);
                child.element.setAttribute('data-x-element', key);
                elementItem.appendChild(child.element);
                this.#element.appendChild(elementItem);
                this.children[key] = child;
            }
        }
        else if(this.#data instanceof XArray)
        {
            this.children = [];
            this.#element = document.createElement('ol');
            this.#attachment.push = (...values) => 
            {
                for(const value of values)
                {
                    const elementItem = document.createElement('li');
                    const child = new XGenerativeDOM(value, this);
                    child.element.setAttribute('data-x-element', '');
                    elementItem.appendChild(child.element);
                    this.#element.appendChild(elementItem);
                    this.children.push(child);
                }
            };
            this.#attachment.pop = () => 
            {
                this.#element.lastElementChild.remove();
                this.children.pop();
            };
            this.#attachment._setValue = () => 
            {
                this.#element.innerHTML = '';
                for(const dataItem of this.#data._value)
                {
                    this.#attachment.push(dataItem);
                }
            };
            this.#attachment._setValue();
        }
        else if(this.#data instanceof XReference)
        {
            this.#element = document.createElement('span');
            const elementKey = document.createElement('b');
            this.child = null;
            let self = false;
            elementKey.contentEditable = true;
            this.#element.appendChild(elementKey);
            this.#attachment._setValue = () =>
            {
                if(!self)
                {
                    elementKey.innerText = this.#data._key;
                }
                if(!xIsNull(this.child))
                {
                    this.child._destroy();
                    this.child = null;
                }
                const newObject = this.#data._value;
                if(!xIsNull(newObject))
                {
                    this.child = new XGenerativeDOM(newObject, this);
                    this.#element.appendChild(this.child.element);
                    elementKey.removeAttribute('data-x-reference-missing');
                }
                else
                {
                    elementKey.setAttribute('data-x-reference-missing', '');
                }
            };
            this.#attachment._setValue();
            elementKey.addEventListener('input', () => 
            {
                self = true;
                this.#data._value = elementKey.innerText;
                self = false;
            });
        }
        else
        {
            this.#element = document.createElement('span');
            this.#element.contentEditable = true;
            let self = false;
            this.#attachment._setValue = () =>
            {
                if(!self)
                {
                    this.#element.innerHTML = String(this.#data.valueOf());
                }
            };
            this.#attachment._setValue();
            
            let setValue = function(setValue)
            {
                return (value) => {
                    self = true;
                    setValue(value);
                    self = false;
                };
            };
            if(this.#data instanceof XBoolean)
            {
                setValue = setValue((value) => 
                {
                    this.#data._value = value;
                });
            }
            else if(this.#data instanceof XInteger)
            {
                setValue = setValue((value) => 
                {
                    this.#data._value = parseInt(value);
                });
            }
            else if(this.#data instanceof XNumber)
            {
                setValue = setValue((value) => 
                {
                    this.#data._value = parseFloat(value);
                });
            }
            else if(this.#data instanceof XString)
            {
                setValue = setValue((value) => 
                {
                    this.#data._value = value.replaceAll('&nbsp;', ' ');
                });
            }
            this.#element.addEventListener('input', () => setValue(this.#element.innerHTML));
        }
    }

    _destroy()
    {
        this.#element.remove();
        this.#element = null;
        this.#data._removeAttachment(this.#attachment);
        if(xIsArray(this.children))
        {
            for(const child of this.children)
            {
                child._destroy();
            }
        }
        else if(xIsObject(this.children))
        {
            if(xIsSpecificClass(this.parent, XGenerativeDOM) && xIsSpecificClass(this.parent.child, XGenerativeDOM))
            {
                // Remove referenced objects
                this.parent.child = null;
            }
            for(const key in this.children)
            {
                this.children[key]._destroy();
            }
        }
        else if(xIsSpecificClass(this.child, XGenerativeDOM))
        {
            this.child._destroy();
        }
    }

    get consoleDestroy()
    {
        this._destroy();
    }

    get parent() { return this.#parent; }
    get element() { return this.#element; }
}

function xIsHTMLTemplateElement(element)
{
    return element?.tagName == 'TEMPLATE';
}

function xGetElementsByDomID(element, domID)
{
    element = xIsHTMLTemplateElement(element) ? element.content : element;
    const elementsByDomID = {};
    const elements = element.children;
    for(const element of elements)
    {
        const xDomId = element.dataset.xDomId;
        if(xDomId !== domID)
        {
            continue;
        }
        let key = xDomId;
        //delete element.dataset.xDomId;
        if(element.dataset.xDomKey)
        {
            key = element.dataset.xDomKey;
            //delete element.dataset.xDomKey;
        }
        if(!(key in elementsByDomID))
        {
            elementsByDomID[key] = [];
        }
        elementsByDomID[key].push(element);
    }
    return elementsByDomID;
}

function xGetNextElementLayer(element, xParentDomId=false, xParentDomKey=false, accessibleLayer={})
{
    let elements = null;
    if(xParentDomId)
    {
        const elementsByDomID = xGetElementsByDomID(element, xParentDomId);
        elements = elementsByDomID[xParentDomKey || xParentDomId] ?? [];
    }
    else
    {
        element = xIsHTMLTemplateElement(element) ? element.content : element;
        elements = element.children;
    }
    for(const element of elements)
    {
        if(!xParentDomId && element.dataset.xDomId)
        {
            continue;
        }
        if('xElement' in element.dataset)
        {
            const xElement = element.dataset.xElement || '$';
            if(!(xElement in accessibleLayer))
            {
                accessibleLayer[xElement] = [];
            }
            accessibleLayer[xElement].push(element);
            continue;
        }
        xGetNextElementLayer(element, undefined, undefined, accessibleLayer);
    }
    return accessibleLayer;
};

export function xExtendClassForDOM(Class)
{
    if('_dom' in Class.prototype)
    {
        if(!('_remount' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_remount', {
                value: function()
                {
                    return this._dom._remount();
                },
                enumerable: false
            });
        }
        if(!('_nextParentArrayDOM' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_nextParentArrayDOM', {
                get()
                {
                    return this._dom._nextParentArrayDOM;
                },
                enumerable: false
            });
        }
        if(!('_nextParentReferenceDOM' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_nextParentReferenceDOM', {
                get()
                {
                    return this._dom._nextParentReferenceDOM;
                },
                enumerable: false
            });
        }
        if(!('_directChildFor' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_directChildFor', {
                value: function(domParent)
                {
                    return this._dom._directChildFor(domParent);
                },
                enumerable: false
            });
        }
        if(!('_elements' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_elements', {
                get()
                {
                    return this._dom._elements;
                },
                enumerable: false
            });
        }
        if(!('_elementFirst' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_elementFirst', {
                get()
                {
                    return this._dom._elementFirst;
                },
                enumerable: false
            });
        }
        if(!('_elementLast' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_elementLast', {
                get()
                {
                    return this._dom._elementLast;
                },
                enumerable: false
            });
        }
        if(!('_flatElements' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_flatElements', {
                get()
                {
                    return this._dom._flatElements;
                },
                enumerable: false
            });
        }
        if(!('_flatElementLast' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_flatElementLast', {
                get()
                {
                    return this._dom._flatElementLast;
                },
                enumerable: false
            });
        }
        if(!('_destroy' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_destroy', {
                value: function()
                {
                    return this._dom._destroy();
                },
                enumerable: false
            });
        }
        if(!('_createStaticMetaInformation' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_createStaticMetaInformation', {
                value: function()
                {
                    return this._dom._createStaticMetaInformation();
                },
                enumerable: false
            });
        }
        if(!('_removeStaticMetaInformation' in Class.prototype))
        {
            Object.defineProperty(Class.prototype, '_removeStaticMetaInformation', {
                value: function()
                {
                    return this._dom._removeStaticMetaInformation();
                },
                enumerable: false
            });
        }
    }
}

export class XTemplateLookupHTML
{
    constructor(templates)
    {
        if(!xIsUndefined(templates))
        {
            if(!xIsObject(templates))
            {
                throw new XTypeError('Templates needs to be a native JavaScript object with native JavaScript strings!', this, templates);
            }
            for(const key in templates)
            {
                this.add(key, templates[key]);
            }
        }
    }

    add(name, template)
    {
        if(!xIsString(name))
        {
            throw new XTypeError('Template name needs to be a native JavaScript string!', this, name);
        }
        if(!xIsString(template))
        {
            throw new XTypeError('Template needs to be a native JavaScript string for name "' + name + '"!', this, template);
        }
        this[name.toUpperCase()] = template;
    }
}

function xGetUnusedID(object, id)
{
    let unusedID = id;
    let i = 1;
    while(unusedID in object)
    {
        unusedID = id + '_' + String(i);
        i += 1;
    }
    return unusedID;
}

function xRenderAccessibleItemDOM(dom, key, settings)
{
    let item = dom[key];
    if(settings.removeNoRenderParts && item._noRender)
    {
        item._destroy();
        return;
    }
    if(!settings.referenceAccessibleDOM || !(key in settings.referenceAccessibleDOM))
    {
        item._render({...settings,
            referenceAccessibleDOM: null
        });
        return;
    }
    const referenceItem = settings.referenceAccessibleDOM[key];
    if(referenceItem instanceof XAccessibleReferenceDOM && !referenceItem._child)
    {
        item._destroy();
        return;
    }
    if(xIsAnySpecificClass(referenceItem, XAccessibleDOM, XAccessibleArrayDOM, XAccessibleReferenceDOM))
    {
        item._render({...settings,
            referenceAccessibleDOM: referenceItem
        });
        return;
    }
    else if('_render' in referenceItem)
    {
        const domReplacement = referenceItem._render(item, settings.context);
        if(domReplacement === null)
        {
            delete dom[key];
            return;
        }
        else if(domReplacement)
        {
            item._elementLast.after(...domReplacement._elements);
            if('xDomId' in item._element.dataset)
            {
                const domID = item._element.dataset.xDomId;
                for(const element of domReplacement._elements)
                {
                    element.dataset.xDomId = domID;
                }
            }
            if('xDomKey' in item._element.dataset)
            {
                const domKey = item._element.dataset.xDomKey;
                for(const element of domReplacement._elements)
                {
                    element.dataset.xDomKey = domKey;
                }
            }
            item._destroy();
            dom[key] = domReplacement;
            if(xIsSpecificClass(domReplacement, XAccessibleDOM))
            {
                item = dom[key];
                item._createStaticMetaInformation();
            }
        }
    }
    if('_dom' in referenceItem)
    {
        item._render({...settings,
            referenceAccessibleDOM: referenceItem._dom
        });
    }
}

export class XEnvironment
{
    #storage;
    #templatesHTML;

    constructor(storage=null)
    {
        this.#storage       = storage;
        this.#templatesHTML = null;
    }

    set templatesHTML(templates)
    {
        this.#templatesHTML = xIsSet(templates) ? (xIsSpecificClass(templates, XTemplateLookupHTML) ? templates : new XTemplateLookupHTML(templates)) : null;
    }

    get storage       () { return this.#storage;       }
    get templatesHTML () { return this.#templatesHTML; }
}

export class XTemplateDOM
{
    static #maxTemplateDepth = 10;
    #environment;
    #parent;
    #elements;
    #elementRoot;
    #itemTemplateDOM;

    constructor(dom, specification, environment=new XEnvironment(), parent=null)
    {
        this.#parent = parent;
        this.#environment = environment;
        if(!xIsSpecificClass(environment, XEnvironment))
        {
            throw new XTypeError('The environment needs to be of type XEnvironment!', this, environment);
        }
        dom = xIsString(dom) ? xTemplateToElements(dom) : dom;
        const domIsHTMLElement = dom instanceof HTMLElement;
        this.#elements = xIsArray(dom) ? dom : [dom];
        if(dom instanceof Document)
        {
            this.#elements = [dom];
            this.#elementRoot = dom;
        }
        else if(domIsHTMLElement)
        {
            this.#elements = [dom];
            if(xIsHTMLTemplateElement(dom))
            {
                this.#elements = Array.from(dom.content.children);
                this.#elementRoot = document.createElement('div');
                this.#elementRoot.append(...this.#elements);
            }
            else
            {
                this.#elementRoot = dom;
            }
        }
        else
        {
            this.#elements = dom;
            this.#elementRoot = document.createElement('div');
            this.#elementRoot.append(...this.#elements);
        }
        if(this.#environment.templatesHTML)
        {
            const templates = this.#environment.templatesHTML;
            for(const element of this.#elements)
            {
                for(let i = 0; i < XTemplateDOM.#maxTemplateDepth; i++)
                {
                    const elementsCustom = Array.from(element.querySelectorAll('*')).filter(element => element.tagName in templates);
                    if(elementsCustom.length == 0)
                    {
                        break;
                    }
                    for(const elementCustom of elementsCustom)
                    {
                        elementCustom.replaceWith(...xTemplateToElements(templates[elementCustom.tagName]));
                    }
                }
            }
        }
        let nextElementLayer = xGetNextElementLayer(this.#elementRoot);
        const isObject = xIsSpecificClass(specification, XObject) || !specification;
        const isArray = xIsSpecificClass(specification, XArray);
        const isReference = xIsSpecificClass(specification, XReference);
        if('$value' in nextElementLayer)
        {
            if(nextElementLayer.$value.length == 1)
            {
                const element = nextElementLayer.$value[0];
                const id = xGetUnusedID(this, 'xId' in element.dataset ? element.dataset['xId'] : 'value');
                const childValue = new XTemplateDOM(element, specification, environment, this);
                Object.defineProperty(this, id, {
                    value: childValue,
                    enumerable: true,
                });
                Object.defineProperty(this, '_value', {
                    value: childValue,
                });
            }
            else
            {
                throw new XTypeError('There has to be exactly 1 $ element for array data!', this, nextElementLayer.$value);
            }
        }
        else if('$referenceValue' in nextElementLayer)
        {
            const types = {};
            for(const element of nextElementLayer.$referenceValue)
            {
                const type = element.dataset.xType ?? '_default';
                element.dataset.xType = type;
                if(type in types)
                {
                    throw new XTypeError('Each type may only exist maximally one time for reference data, but "' + type + '" was found at least twice!', this, types);
                }
                types[type] = element;
            }
            for(const type in types)
            {
                const elementCopy = this.#elementRoot.cloneNode(true);
                const nextElementLayerInReference = xGetNextElementLayer(elementCopy);
                let elementTypeValue = null;
                for(const element of nextElementLayerInReference.$referenceValue)
                {
                    if(element.dataset.xType == type)
                    {
                        elementTypeValue = element;
                    }
                    else
                    {
                        element.remove();
                    }
                }
                elementTypeValue.dataset.xElement = '$value';
                const childValue = new XTemplateDOM(Array.from(elementCopy.children), environment.storage?.types[type], environment, this);
                Object.defineProperty(this, type, {
                    value: childValue,
                });
            }
            if(!('_default' in types))
            {
                const childValue = new XTemplateDOM([], null, environment, this);
                Object.defineProperty(this, '_default', {
                    value: childValue,
                });
            }
        }
        else if(isObject)
        {
            if('$static' in nextElementLayer)
            {
                if(nextElementLayer.$static.length == 1)
                {
                    const element = nextElementLayer.$static[0];
                    delete element.dataset.xElement;
                    let elementToAddAfter = element;
                    for(const specificationKey in specification._value)
                    {
                        if(specificationKey in nextElementLayer)
                        {
                            continue;
                        }
                        const elementCopy = element.cloneNode(true);
                        const nextElementLayerInObject = xGetNextElementLayer(elementCopy);
                        let elementValue = elementCopy;
                        let elementsToAdd = [elementCopy];
                        if(!xIsObjectEmpty(nextElementLayerInObject))
                        {
                            if('$key' in nextElementLayerInObject)
                            {
                                if(nextElementLayerInObject.$key.length == 1)
                                {
                                    const elementKey = nextElementLayerInObject.$key[0];
                                    elementKey.innerHTML = specificationKey;
                                    delete elementKey.dataset.xElement;
                                }
                                else
                                {
                                    throw new XTypeError('There has to be exactly 1 $key element!', this, nextElementLayerInObject.$value);
                                }
                            }
                            if('$value' in nextElementLayerInObject)
                            {
                                if(nextElementLayerInObject.$value.length == 1)
                                {
                                    elementValue = nextElementLayerInObject.$value[0];
                                }
                                else
                                {
                                    throw new XTypeError('There has to be exactly 1 $value element!', this, nextElementLayerInObject.$value);
                                }
                            }
                        }
                        if(xIsHTMLTemplateElement(elementCopy))
                        {
                            elementsToAdd = Array.from(elementCopy.content.childNodes);
                        }
                        elementValue.dataset.xElement = specificationKey;
                        elementToAddAfter.after(...elementsToAdd);
                        elementToAddAfter = elementsToAdd[elementsToAdd.length-1];
                    }
                    element.remove();
                }
                else
                {
                    throw new XTypeError('There has to be exactly 1 $static element!', this, nextElementLayer.$static);
                }
            }

            nextElementLayer = xGetNextElementLayer(this.#elementRoot);
            if(this.#elementRoot != dom)
            {
                this.#elements = Array.from(this.#elementRoot.children);
            }
            for(const key in nextElementLayer)
            {
                for(let i = 0; i < nextElementLayer[key].length; i++)
                {
                    const element = nextElementLayer[key][i];
                    const id = xGetUnusedID(this, element.dataset.xId || key);
                    const childDOM = new XTemplateDOM(element, specification && key in specification._value ? specification._value[key] : specification, environment, this);
                    Object.defineProperty(this, id, {
                        value: childDOM,
                        enumerable: true,
                    });
                }
            }
        }
        else if(isReference && domIsHTMLElement && dom.tagName == 'INPUT')
        {
            dom.value = specification._key;
        }
        else if(isArray || isReference)
        {
            if(!domIsHTMLElement)
            {
                throw new XTypeError('For array data, the provided dom needs to be an HTMLElement!', this, dom);
            }
            const valueName          = isArray ? '$value'            : '$referenceValue';
            const childSpecification = isArray ? specification._type : specification    ;
            const elementAnchor = document.createElement('template');
            elementAnchor.dataset.xElement = dom.dataset.xElement;
            dom.before(elementAnchor);
            const index = this.#parent?._elements.indexOf(dom);
            if(index >= 0)
            {
                this.#parent._elements.splice(index, 1, elementAnchor);
            }
            dom.remove();

            if('$' in nextElementLayer)
            {
                for(const element of nextElementLayer.$)
                {
                    element.dataset.xElement = valueName;
                }
            }
            if(dom.dataset.xId)
            {
                elementAnchor.dataset.xId = dom.dataset.xId;
            }
            if(xIsHTMLTemplateElement(dom))
            {
                if(this.#elements.length == 0)
                {
                    const elementValue = document.createElement('template');
                    elementValue.dataset.xElement = valueName;
                    this.#elements = [elementValue];
                }
            }
            else
            {
                if('$' in nextElementLayer)
                {
                    delete dom.dataset.xElement;
                }
                else
                {
                    dom.dataset.xElement = valueName;
                }
                delete dom.dataset.xId;
            }
            this.#itemTemplateDOM = new XTemplateDOM(this.#elements, childSpecification, environment, this);
            this.#elements = [elementAnchor];
        }
        else if(specification instanceof XPrimitive)
        {
            for(const element of this.#elements)
            {
                if(element.children.length == 0)
                {
                    if(element.tagName == 'INPUT' || element.tagName == 'TEXTAREA' || element.tagName == 'SELECT')
                    {
                        element.value = specification.valueOf();
                    }
                    else
                    {
                        element.innerHTML = specification.toString();
                    }
                }
            }
        }
    }

    _createAccessibleDOM(data, settings = {})
    {
        return new XAccessibleDOM(this, data, {...settings});
    }

    *[Symbol.iterator]() 
    {
        for(const key of Object.keys(this)) 
        {
            yield this[key];
        }
    }

    get _environment     () { return this.#environment;                       }
    get _parent          () { return this.#parent;                            }
    get _element         () { return this.#elements[0];                       }
    get _elementFirst    () { return this.#elements[0];                       }
    get _elementLast     () { return this.#elements[this.#elements.length-1]; }
    get _elements        () { return this.#elements;                          }
    get _hasElements     () { return this.#elements.length > 0;               }
    get _elementRoot     () { return this.#elementRoot;                       }
    get _itemTemplateDOM () { return this.#itemTemplateDOM;                   }
};

class XAccessibleArrayDOM extends Array
{
    #parent;
    #data;
    #elementAnchor;
    #attachmentHandler;
    #templateDOM;
    #itemTemplateDOM;
    #childSettings;
    #creator;

    constructor(elementAnchor, data, settings = {})
    {
        super();
        this.#data = data;
        this.#parent = settings.parent;
        if(this.#parent)
        {
            // Caution: splice, e.g., uses [Symbol.species] to create a 
            // new array instance to be returned, hence we check for
            // a valid parent to avoid this issue
            this.#elementAnchor = elementAnchor;
            this.#childSettings = {...settings,
                parent: this,
            };
            this.#templateDOM = settings.templateDOM;
            const itemTemplateDOM = this.#templateDOM?._itemTemplateDOM;
            if(itemTemplateDOM)
            {
                this.#attachmentHandler = new XAttachmentHandler();
                this.#itemTemplateDOM = itemTemplateDOM;
                this.#attachmentHandler.add(this.#data, this);
            }
            else
            {
                const xLength = parseInt(this.#elementAnchor.dataset.xLength);
                const xParentDomId = this.#elementAnchor.dataset.xParentDomId;
                for(let i = 0; i < xLength; i++)
                {
                    super.push(new XAccessibleDOM(this.#elementAnchor.parentElement, null, {
                        parent: this,
                        _xParentDomId  : xParentDomId,
                        _xParentDomKey : String(i),
                    }));
                }
            }
        }
    }

    _createProxy()
    {
        let isReferenceArray = false;
        if(this.#itemTemplateDOM)
        {
            isReferenceArray = xIsSpecificClass(this.#data._type, XReference);
        }
        else
        {
            isReferenceArray |= this.length > 0 && this[0]._value instanceof XAccessibleReferenceDOM;
        }
        return new Proxy(this, isReferenceArray ? 
        {
            get(target, propertyKey, receiver)
            {
                if(propertyKey == Symbol.iterator)
                {
                    return function* ()
                    {
                        for(let i = 0; i < target.length; i++)
                        {
                            yield target[i]._value._child._value.mount;
                        }
                    }
                }
                else if(/^\d+$/.test(propertyKey)) 
                {
                    return target[propertyKey]?._value?._child?._value?.mount;
                }
                return Reflect.get(target, propertyKey, receiver);
            }
        } :
        {
            get(target, propertyKey, receiver)
            {
                if(propertyKey == Symbol.iterator)
                {
                    return function* ()
                    {
                        for(let i = 0; i < target.length; i++)
                        {
                            yield target[i]._value.mount;
                        }
                    }
                }
                else if(/^\d+$/.test(propertyKey)) 
                {
                    return target[propertyKey]?._value?.mount;
                }
                return Reflect.get(target, propertyKey, receiver);
            }
        });
    }

    _setCreator(creator, returnProxy=true)
    {
        if(!this.#creator)
        {
            this.#creator = creator;
            if(this.#itemTemplateDOM)
            {
                this.#attachmentHandler.add(this.#data, this);
            }
            else
            {
                if(this.length > 0 && this[0]._value instanceof XAccessibleReferenceDOM)
                {
                    for(const item of this)
                    {
                        item._value._setCreator(this.#creator(item._value, this));
                    }
                }
                else
                {
                    for(const item of this)
                    {
                        item._value._mount(this.#creator);
                    }
                }
            }
        }
        return returnProxy ? this._createProxy() : this;
    }

    _attachmentAdded(attachment, callCreate=true)
    {
        if(callCreate && '_create' in attachment)
        {
            for(const child of this)
            {
                attachment._create(child);
            }
        }
    }

    #clear()
    {
        for(const child of this)
        {
            child._destroy();
        }
        this.length = 0;
    }

    _destroy()
    {
        this.#attachmentHandler?.destroy();
        this.#clear();
        this._destroyAttachments(this);
        this.#elementAnchor.remove();
    }

    _setValue(dataArray)
    {
        this.#clear();
        for(let i = 0; i < dataArray.length; i++)
        {
            this.push(dataArray._value[i]);
        }
    }

    _referenceChanged(index, value)
    {
        let child = this.#createChild(value);
        this[index]._flatElementLast.after(...child._elements);
        this[index]._destroy();
        this[index] = child;
    }

    _remove(indexOrElement)
    {
        const index = xIsInteger(indexOrElement) ? indexOrElement : this.indexOf(indexOrElement);
        if(index >= 0)
        {
            if(this.#data)
            {
                this.#data.splice(index, 1);
            }
            else
            {
                this.splice(index, 1);
            }
        }
        return index;
    }

    _removeDirectChild(dom)
    {
        return this._remove(dom._directChildFor(this));
    }

    #createChild(data)
    {
        let child = this.#itemTemplateDOM._createAccessibleDOM(data, this.#childSettings);
        if(this.#creator)
        {
            if(xIsSpecificClass(child._value, XAccessibleReferenceDOM))
            {
                child._value._setCreator(this.#creator(data, child));
            }
            else
            {
                child._value._mount(this.#creator(data, child));
            }
        }
        if(child._dom instanceof XAccessibleDOM)
        {
            child._dom._parent = this;
        }
        return child;
    }

    push(...values)
    {
        for(const value of values)
        {
            let elementAnchor = this.#elementAnchor;
            if(this.length > 0)
            {
                elementAnchor = this._flatElementLast;
            }
            let child = this.#createChild(value);
            if(child)
            {
                super.push(child);
                elementAnchor.after(...child._flatElements);
            }
            this._callAttachments('_create', child);
        }
        return this.length;
    }

    pop()
    {
        if(this.length > 0)
        {
            const child = this[this.length-1];
            this._destroyAttachments(child);
            child._destroy();
        }
        super.pop();
    }

    splice(start, deleteCount, ...values)
    {
        deleteCount = Math.max(deleteCount, 0);
        start = Math.min(this.length, Math.max(-this.length, start));
        if(start < 0)
        {
            start = this.length + start;
        }
        const end = Math.min(start + deleteCount, this.length);
        for(let i = start; i < end; i++)
        {
            const child = this[i];
            this._destroyAttachments(child);
            child._destroy();
        }
        const newItems = [];
        for(const value of values)
        {
            let child = this.#createChild(value);
            if(child)
            {
                newItems.push(child);
            }
        }
        let elementAnchor = this.#elementAnchor;
        if(start > 0)
        {
            elementAnchor = this[start-1]._flatElementLast;
        }
        const elements = [];
        for(const child of newItems)
        {
            elements.push(...child._flatElements);
        }
        if(elements)
        {
            elementAnchor.after(...elements);
        }
        super.splice(start, deleteCount, ...newItems);
        for(const newItem of newItems)
        {
            this._callAttachments('_create', newItem);
        }
    }

    _attach(attachment, callSetValueOrChanged = true)
    {
        if(!this.#data)
        {
            console.warn('Cannot attach to invalid data', this);
            return;
        }
        this.#attachmentHandler.add(this.#data, attachment, callSetValueOrChanged);
    }

    toJSON()
    {
        const content = [];
        for(const item of this)
        {
            if('toJSON' in item)
            {
                content.push(item.toJSON());
            }
            else if('_dom' in item)
            {
                content.push(item._dom.toJSON());
            }
        }
        return content;
    }

    _createStaticMetaInformation()
    {
        const domID = xRandomString();
        this.#elementAnchor.dataset.xParentDomId = domID;
        this.#elementAnchor.dataset.xDomType = 'XArray';
        this.#elementAnchor.dataset.xLength = this.length;
        for(let i = 0; i < this.length; i++)
        {
            const item = this[i];
            item._createStaticMetaInformation();
            for(const element of item._elements)
            {
                element.dataset.xDomId = domID;
                element.dataset.xDomKey = i;
            }
        }
    }

    _removeStaticMetaInformation()
    {
        delete this.#elementAnchor.dataset.xParentDomId;
        delete this.#elementAnchor.dataset.xDomType;
        delete this.#elementAnchor.dataset.xLength;
        for(let i = 0; i < this.length; i++)
        {
            const item = this[i];
            item._removeStaticMetaInformation();
            for(const element of item._elements)
            {
                delete element.dataset.xDomId;
                delete element.dataset.xDomKey;
            }
        }
    }

    _render(settings)
    {
        for(let i = 0; i < this.length; i++)
        {
            xRenderAccessibleItemDOM(this, i, settings);
        }
    }

    _directChildFor(domParent)
    {
        if(!this.#parent)
        {
            return null;
        }
        if(this.#parent == domParent)
        {
            return this;
        }
        return this.#parent._directChildFor(domParent);
    }

    get _flatElements()
    {
        const elements = [this.#elementAnchor];
        for(const child of this)
        {
            elements.push(...child._flatElements);
        }
        return elements;
    }

    get _flatElementLast()
    {
        return this.length == 0 ? this.#elementAnchor : this[this.length-1]._flatElementLast;
    }

    get _nextParentReferenceDOM()
    {
        return this.#parent?._nextParentReferenceDOM;
    }

    get _nextParentArrayDOM()
    {
        return this;
    }

    get _element      () { return this.#elementAnchor;   }
    get _elementFirst () { return this.#elementAnchor;   }
    get _elementLast  () { return this.#elementAnchor;   }
    get _elements     () { return [this.#elementAnchor]; }
    get _data         () { return this.#data;            }
    get _parent       () { return this.#parent;          }
};
xExtendClassForAttachments(XAccessibleArrayDOM);

class XAccessibleReferenceDOM
{
    #parent;
    #data;
    #elementAnchor;
    #attachmentHandler;
    #templateDOM;
    #itemTemplateDOM;
    #childSettings;
    #creator;
    #child;
    #enabled = true;

    constructor(elementAnchor, data, settings = {})
    {
        this.#data = data;
        this.#parent = settings.parent;
        this.#elementAnchor = elementAnchor;
        this.#childSettings = {...settings,
            parent: this,
        };
        this.#templateDOM = settings.templateDOM;
        this.#child = null;
        const itemTemplateDOM = this.#templateDOM?._itemTemplateDOM;
        if(itemTemplateDOM)
        {
            this.#attachmentHandler = new XAttachmentHandler();
            this.#itemTemplateDOM = itemTemplateDOM;
            this.#attachmentHandler.add(this.#data, this);
        }
        else
        {
            const xParentDomId = this.#elementAnchor.dataset.xParentDomId;
            if(xParentDomId)
            {
                this.#child = new XAccessibleDOM(this.#elementAnchor.parentElement, null, {
                    parent: this,
                    _xParentDomId: xParentDomId
                });
            }
        }
    }

    _setCreator(creator)
    {
        if(this.#creator)
        {
            return this;
        }
        this.#creator = xIsFunction(creator) ? {_default: creator} : creator;
        if(!('_default' in this.#creator))
        {
            this.#creator._default = (data) => {
                console.warn('Type "' + data._typename + '" has no XDOM representation. You can use "_default" in the creator as a fallback for references.');
                return null;
            };
        }
        if(this.#itemTemplateDOM)
        {
            this.#attachmentHandler.add(this.#data, this);
        }
        else
        {
            if(this.#child)
            {
                const type = this.#child._value._element.dataset.xType;
                this.#child._value._mount(type in this.#creator ? this.#creator[type] : this.#creator._default);
            }
        }
        return this;
    }

    _attachmentAdded(attachment, callCreate=true)
    {
        if(callCreate && this.#child && '_create' in attachment)
        {
            attachment._create(this.#child);
        }
    }

    #clear()
    {
        if(this.#child)
        {
            this.#child._destroy();
            this.#child = null;
        }
    }

    _destroy()
    {
        this.#attachmentHandler?.destroy();
        this.#clear();
        this._destroyAttachments(this);
        this.#elementAnchor.remove();
    }

    _setValue(dataReference, oldKey)
    {
        if(!this.#enabled || dataReference._key == oldKey)
        {
            return;
        }
        this.#clear();
        let data = dataReference._value;
        if(data)
        {
            this.#child = this.#itemTemplateDOM[data._typename in this.#itemTemplateDOM ? data._typename : '_default']._createAccessibleDOM(data, this.#childSettings);
            if(this.#creator)
            {
                const type = data._typename in this.#creator ? data._typename : '_default';
                this.#child._value._mount(this.#creator[type](data, this.#child));
                this.#child._value._element.dataset.xType = type;
            }
            if(this.#child?._dom instanceof XAccessibleDOM)
            {
                this.#child._dom._parent = this;
            }
            this._callAttachments('_create', this.#child);
            if(this.#child)
            {
                this.#elementAnchor.after(...this.#child._flatElements);
            }
        }
    }

    _enable(enable=true)
    {
        if(this.#enabled == enable)
        {
            return;
        }
        this.#enabled = enable;
        if(this.#enabled)
        {
            this._setValue(this.#data);
        }
        else
        {
            this.#clear();
        }
    }

    _disable(disable=true)
    {
        this._enable(!disable);
    }

    _remount()
    {
        this._setValue(this.#data);
    }

    _attach(attachment, callSetValueOrChanged = true)
    {
        if(!this.#data)
        {
            console.warn('Cannot attach to invalid data', this);
            return;
        }
        this.#attachmentHandler.add(this.#data, attachment, callSetValueOrChanged);
    }

    toJSON()
    {
        if(this.#child)
        {
            if('toJSON' in this.#child)
            {
                return this.#child.toJSON();
            }
            else if('_dom' in this.#child)
            {
                return this.#child._dom.toJSON();
            }
        }
        return null;
    }

    _createStaticMetaInformation()
    {
        if(this.#child)
        {
            const domID = xRandomString();
            this.#elementAnchor.dataset.xParentDomId = domID;
            this.#elementAnchor.dataset.xDomType = 'XReference';
            this.#child._createStaticMetaInformation();
            for(const element of this.#child._elements)
            {
                element.dataset.xDomId = domID;
            }
        }
    }

    _removeStaticMetaInformation()
    {
        delete this.#elementAnchor.dataset.xParentDomId;
        delete this.#elementAnchor.dataset.xDomType;
        if(this.#child)
        {
            this.#child._removeStaticMetaInformation();
            for(const element of this.#child._elements)
            {
                delete element.dataset.xDomId;
            }
        }
    }

    _render(settings)
    {
        if(this.#child)
        {
            xRenderAccessibleItemDOM(this, '_child', settings);
        }
    }

    _directChildFor(domParent)
    {
        if(!this.#parent)
        {
            return null;
        }
        if(this.#parent == domParent)
        {
            return this;
        }
        return this.#parent._directChildFor(domParent);
    }

    get _flatElements()
    {
        const elements = [this.#elementAnchor];
        if(this.#child)
        {
            elements.push(...this.#child._flatElements);
        }
        return elements;
    }

    get _flatElementLast()
    {
        return this.#child ? this.#child._flatElementLast : this.#elementAnchor;
    }

    get mount () 
    { 
        return this.#child?._value?.mount;           
    }

    get _nextParentReferenceDOM()
    {
        return this;
    }

    get _nextParentArrayDOM()
    {
        return this.#parent?._nextParentArrayDOM;
    }

    get _element      () { return this.#elementAnchor;   }
    get _elementFirst () { return this.#elementAnchor;   }
    get _elementLast  () { return this.#elementAnchor;   }
    get _elements     () { return [this.#elementAnchor]; }
    get _data         () { return this.#data;            }
    get _parent       () { return this.#parent;          }
    get _child        () { return this.#child;           }
};
xExtendClassForAttachments(XAccessibleReferenceDOM);

export class XAccessibleDOM
{
    #parent;
    #data;
    #elements;
    #attachmentHandler;
    #customData;
    #noRender;
    #templateDOM;

    constructor(dom, data, {
        bindDataToElements = true, 
        bindInputsToData   = true, 
        bindElementsToData = false, 
        templateDOM        = null,
        parent             = null,
        noRender           = false,
        _xParentDomId      = false,
        _xParentDomKey     = false,
    } = {})
    {
        this.#noRender = noRender;
        this.#templateDOM = templateDOM;
        this.#parent = parent;
        this.#data = data;
        let elementRoot = dom;
        if(xIsSpecificClass(dom, XTemplateDOM))
        {
            this.#templateDOM = dom;
            elementRoot = dom._elementRoot.cloneNode(true);
            this.#elements = dom._elementRoot == dom._elementFirst ? [elementRoot] : Array.from(elementRoot.children);
        }
        else if(dom instanceof HTMLElement)
        {
            this.#elements = [dom];
            if(dom.dataset.xDomType === 'XMount')
            {
                const mountDOM = dom.dataset.xParentDomId ?
                    new XAccessibleDOM(dom.parentElement, null, {
                        parent: this,
                        _xParentDomId: dom.dataset.xParentDomId
                    }) :
                    new XAccessibleDOM(Array.from(dom.children), null, {
                        parent: this
                    });
                Object.defineProperty(this, 'mount', {
                    value: mountDOM,
                    enumerable: true,
                    configurable: true,
                    writable: true,
                });
                return;
            }
        }
        else if(xIsArray(dom))
        {
            elementRoot = {
                children: dom
            };
            this.#elements = dom;
        }
        if(_xParentDomId)
        {
            const elementsByDomID = xGetElementsByDomID(dom, _xParentDomId);
            if(_xParentDomKey in elementsByDomID)
            {
                this.#elements = elementsByDomID[_xParentDomKey];
            }
            else if(_xParentDomId in elementsByDomID)
            {
                this.#elements = elementsByDomID[_xParentDomId];
            }
        }
        const childSettings = {
            bindDataToElements : bindDataToElements,
            bindInputsToData   : bindInputsToData,
            bindElementsToData : bindElementsToData,
            templateDOM        : this.#templateDOM,
            parent             : this,
            noRender           : noRender,
            _xParentDomId      : false,        
        };

        const nextElementLayer = xGetNextElementLayer(elementRoot, _xParentDomId, _xParentDomKey);
        if('$value' in nextElementLayer)
        {
            if(nextElementLayer.$value.length == 1)
            {
                const element = nextElementLayer.$value[0];
                const id = xGetUnusedID(this, element.dataset.xId || 'value');
                childSettings.templateDOM = this.#templateDOM?._value;
                let childDOM = null;
                if(xIsSpecificClass(this.#data, XArray) || element.dataset.xDomType === 'XArray')
                {
                    childDOM = new XAccessibleArrayDOM(element, this.#data, childSettings);
                }
                else if(xIsSpecificClass(this.#data, XReference) || element.dataset.xDomType === 'XReference')
                {
                    childDOM = new XAccessibleReferenceDOM(element, this.#data, childSettings);
                }
                else
                {
                    childDOM = new XAccessibleDOM(element, this.#data, childSettings);
                }
                Object.defineProperty(this, id, {
                    value: childDOM,
                    enumerable: true,
                    configurable: true,
                    writable: true,
                });
                Object.defineProperty(this, '_value', {
                    value: childDOM
                });
            }
            else
            {
                throw new XTypeError('There has to be exactly 1 $ element for array data!', this, nextElementLayer.$value);
            }
            delete nextElementLayer.$value;
        }
        else if(this.#data instanceof XPrimitive || xIsSpecificClass(this.#data, XReference))
        {
            this.#bindElementsAsPrimitive(bindDataToElements, bindInputsToData, bindElementsToData);
        }
        
        for(const key in nextElementLayer)
        {
            if(key.startsWith('$'))
            {
                continue;
            }
            let childData = null;
            if(xIsSpecificClass(this.#data, XObject))
            {
                childData = this.#data;
                if(key in this.#data._value)
                {
                    childData = this.#data._value[key];
                }
            } 
            for(let i = 0; i < nextElementLayer[key].length; i++)
            {
                const element = nextElementLayer[key][i];
                const id = xGetUnusedID(this, element.dataset.xId || key);
                childSettings.templateDOM = this.#templateDOM?.[id];
                let childDOM = null;
                if(xIsSpecificClass(childData, XArray) || element.dataset.xDomType === 'XArray')
                {
                    childDOM = new XAccessibleArrayDOM(element, childData, childSettings);
                }
                else if(xIsSpecificClass(childData, XReference) || element.dataset.xDomType === 'XReference')
                {
                    if(element.tagName == 'INPUT')
                    {
                        childDOM = new XAccessibleDOM(element, childData, childSettings);
                    }
                    else
                    {
                        childDOM = new XAccessibleReferenceDOM(element, childData, childSettings);
                    }
                    /*
                    if(element.tagName == 'INPUT')
                    {
                        this.#bindElementsAsPrimitive(bindDataToElements, bindInputsToData, bindElementsToData);
                    }
                    else
                    {
                        
                    }
                    */
                }
                else
                {
                    childDOM = new XAccessibleDOM(element, childData, {...childSettings,
                        noRender: 'xNoRender' in element.dataset
                    });
                }
                if(childDOM)
                {
                    Object.defineProperty(this, id, {
                        value: childDOM,
                        enumerable: true,
                        configurable: true,
                        writable: true,
                    });
                }
            }
        }

        if(xIsHTMLTemplateElement(dom) && this.#templateDOM?._hasElements)
        {
            this._mount(this.#templateDOM._createAccessibleDOM(this.#data, childSettings));
        }
    }

    #bindElementsAsPrimitive(bindDataToElements=false, bindInputsToData=false, bindElementsToData=false)
    {
        if(!this.#data)
        {
            return;
        }
        if(bindElementsToData)
        {
            bindInputsToData = true;
        }
        for(const element of this.#elements)
        {
            let self = false;
            let setValueFromElement = null;
            if(bindInputsToData)
            {
                setValueFromElement = function(setValue)
                {
                    return (value) => {
                        self = true;
                        setValue(value);
                        self = false;
                    };
                };
                if(this.#data instanceof XBoolean)
                {
                    setValueFromElement = setValueFromElement((value) => this.#data._value = !!value);
                }
                else if(this.#data instanceof XInteger)
                {
                    setValueFromElement = setValueFromElement((value) => this.#data._value = parseInt(value));
                }
                else if(this.#data instanceof XNumber)
                {
                    setValueFromElement = setValueFromElement((value) => this.#data._value = parseFloat(value));
                }
                else if(this.#data instanceof XString)
                {
                    setValueFromElement = setValueFromElement((value) => this.#data._value = value.replaceAll('&nbsp;', ' '));
                }
                else if(this.#data instanceof XReference)
                {
                    setValueFromElement = setValueFromElement((value) => this.#data._value = value);
                }
            }
            if(element.tagName == 'INPUT' || element.tagName == 'TEXTAREA' || element.tagName == 'SELECT')
            {
                if(this.#data instanceof XReference)
                {
                    element.value = this.#data._key;
                    if(bindDataToElements)
                    {
                        this._attach({
                            _setValue: () =>
                            {
                                if(!self)
                                {
                                    element.value = this.#data._key;
                                }
                            }
                        }, false);
                    }
                }
                else
                {
                    element.value = this.#data.valueOf();
                    if(bindDataToElements)
                    {
                        this._attach({
                            _setValue: () =>
                            {
                                if(!self)
                                {
                                    element.value = this.#data.valueOf();
                                }
                            }
                        }, false);
                    }
                }
                if(bindInputsToData)
                {
                    element.addEventListener('input', () => setValueFromElement(element.value));
                }
            }
            else
            {
                element.innerHTML = this.#data.toString();
                if(bindDataToElements)
                {
                    this._attach({
                        _setValue: () =>
                        {
                            if(!self)
                            {
                                element.innerHTML = this.#data.toString();
                            }
                        }
                    }, false);
                }
                let isContentEditable = element.isContentEditable || element.contentEditable === 'true' || element.contentEditable === 'plaintext-only';
                if(!isContentEditable && bindElementsToData)
                {
                    element.contentEditable = 'plaintext-only';
                    isContentEditable = true;
                }
                if(bindInputsToData || isContentEditable)
                {
                    element.addEventListener('input', () => setValueFromElement(element.innerHTML));
                }
            }
        }
    }

    get #getOrCreateAttachmentHandler()
    {
        if(!this.#attachmentHandler)
        {
            this.#attachmentHandler = new XAttachmentHandler();
        }
        return this.#attachmentHandler;
    }

    get _customData()
    {
        if(!this.#customData)
        {
            this.#customData = {};
        }
        return this.#customData;
    }

    _destroy()
    {
        const elements = this.#elements;
        this.#elements = [];
        this.#attachmentHandler?.destroy();
        for(const child of this)
        {
            child._destroy();
        }
        this._destroyAttachments(this);
        for(const element of elements)
        {
            element.remove?.();
            const index = this.#parent?._elements.indexOf(element);
            if(index >= 0)
            {
                this.#parent._elements.splice(index, 1);
            }
        }

    }

    _attach(attachment, callSetValueOrChanged = true)
    {
        if(!this.#data)
        {
            console.warn('Cannot attach to invalid data', this);
            return;
        }
        this.#getOrCreateAttachmentHandler.add(this.#data, attachment, callSetValueOrChanged);
    }

    _attachData(data, attachment, callSetValueOrChanged = true)
    {
        this.#getOrCreateAttachmentHandler.add(data, attachment, callSetValueOrChanged);
    }

    _computedOutput(output, compute, ...parameters)
    {
        return xComputedOutput(this.#getOrCreateAttachmentHandler, output, compute, ...parameters);
    }

    _computed(compute, ...parameters)
    {
        xComputed(this.#getOrCreateAttachmentHandler, compute, ...parameters);
    }

    _bind(data, {
        bindDataToElements = true, 
        bindInputsToData   = true,
        bindElementsToData = false,
    } = {})
    {
        this.#data = data;
        this.#bindElementsAsPrimitive(bindDataToElements, bindInputsToData, bindElementsToData);
    }

    _bindComputed(output, compute, ...parameters)
    {
        this.#data = xComputedOutput(this.#getOrCreateAttachmentHandler, output, compute, ...parameters);
        this.#bindElementsAsPrimitive(true, false, false);
        return this.#data;
    }

    _mount(accessibleDOM)
    {
        if(this.mount)
        {
            if(xIsSpecificClass(this.mount, XAccessibleDOM) && this.mount._hasElements)
            {
                if(this._element.dataset.xDomType === 'XMount')
                {
                    Object.defineProperty(this, 'mount', {
                        value: accessibleDOM(this.mount),
                        enumerable: true,
                        configurable: true,
                        writable: true,
                    });
                }
            }
            return this.mount;
        }
        if(this.#elements.length == 1)
        {
            if(xIsFunction(accessibleDOM))
            {
                accessibleDOM = accessibleDOM();
            }
            if(accessibleDOM)
            {
                if(accessibleDOM._elements)
                {
                    if(xIsHTMLTemplateElement(this._element))
                    {
                        this._element.after(...accessibleDOM._flatElements);
                    }
                    else
                    {
                        this._element.append(...accessibleDOM._flatElements);
                    }
                }
                if(accessibleDOM._dom instanceof XAccessibleDOM)
                {
                    accessibleDOM._dom._parent = this;
                }
                Object.defineProperty(this, 'mount', {
                    value: accessibleDOM,
                    enumerable: true,
                    configurable: true,
                    writable: true,
                });
                return this.mount;
            }
            return null;
        }
    }

    _unmount(destroy=true)
    {
        if(this.mount)
        {
            const mount = this.mount;
            delete this.mount;
            if(destroy)
            {
                mount._destroy();
            }
            return mount;
        }
    }

    _remount(destroy=true)
    {
        const mount = this._unmount(destroy);
        this._mount();
        const firstParentReferenceDOM = this._nextParentReferenceDOM;
        firstParentReferenceDOM._remount();
    }

    toJSON()
    {
        const content = {};
        for(const key in this)
        {
            const item = this[key];
            if('toJSON' in item)
            {
                content[key] = item.toJSON();
            }
            else if('_dom' in item)
            {
                content[key] = item._dom.toJSON();
            }
        }
        return content;
    }

    _createStaticMetaInformation()
    {
        if(this.mount)
        {
            if(xIsHTMLTemplateElement(this._element))
            {
                const domID = xRandomString();
                this._element.dataset.xParentDomId = domID;
                for(const element of this.mount._elements)
                {
                    element.dataset.xDomId = domID;
                }
            }
            this._element.dataset.xDomType = 'XMount';
            this.mount._createStaticMetaInformation();
        }
        else
        {
            for(const key in this)
            {
                this[key]._createStaticMetaInformation();
            }
        }
    }

    _removeStaticMetaInformation()
    {
        if(this.mount)
        {
            delete this._element.dataset.xParentDomId;
            delete this._element.dataset.xDomType;
            for(const element of this.mount._elements)
            {
                delete element.dataset.xDomId;
            }
            this.mount._removeStaticMetaInformation();
        }
        else
        {
            for(const key in this)
            {
                this[key]._removeStaticMetaInformation();
            }
        }
    }

    _render({
        context                = '', 
        removeNoRenderParts    = true,
        referenceAccessibleDOM = undefined, 
    } = {})
    {
        const settings = {
            context                : context,
            removeNoRenderParts    : removeNoRenderParts,
            referenceAccessibleDOM : referenceAccessibleDOM,
        }
        if(referenceAccessibleDOM === undefined)
        {
            this._createStaticMetaInformation();
            let elementRoot = null;
            const isRoot = this.#elements.length == 1;
            if(isRoot)
            {
                if(this._element instanceof Document)
                {
                    elementRoot = this._element.documentElement.cloneNode(true);
                }
                else
                {
                    elementRoot = this._element.cloneNode(true);
                }
            }
            else
            {
                elementRoot = document.createElement('div');
                for(const element of this._flatElements)
                {
                    elementRoot.append(element.cloneNode(true));
                }
            }
            this._removeStaticMetaInformation();
            const dom = new XAccessibleDOM(elementRoot);
            dom._render({...settings,
                referenceAccessibleDOM: this
            });
            if(!isRoot)
            {
                dom.#elements = Array.from(elementRoot.children);
            }
            return dom;
        }
        for(const key in this)
        {
            xRenderAccessibleItemDOM(this, key, settings);
        }
    }

    _directChildFor(domParent)
    {
        if(!this.#parent)
        {
            return null;
        }
        if(this.#parent == domParent)
        {
            return this;
        }
        return this.#parent._directChildFor(domParent);
    }

    get _flatElements()
    {
        const elements = Array.from(this.#elements);
        if(xIsHTMLTemplateElement(this._element) && this.mount)
        {
            elements.push(...this.mount._flatElements);
        }
        else
        {
            for(const child of this)
            {
                const index = elements.indexOf(child._element);
                if(index >= 0)
                {
                    elements.splice(index, 1, ...child._flatElements);
                }
            }
        }
        return elements;
    }

    get _flatElementLast()
    {
        if(xIsHTMLTemplateElement(this._element) && this.mount)
        {
            return this.mount._flatElementLast;
        }
        const elementLast = this._elementLast;
        if(xIsHTMLTemplateElement(elementLast))
        {
            for(const child of this)
            {
                if(child._element == elementLast)
                {
                    return child._flatElementLast;
                }
            }
        }
        return elementLast;
    }

    _changeElementType(elementType, index=0)
    {
        this.#elements.splice(index, 1, xChangeElementType(this.#elements[index], elementType));
    }

    _wrapElements(elementType)
    {
        // Careful: for static page building, this currently only works if the parent does not have a flat structure!
        const elementWrap = document.createElement(elementType);
        this._elementFirst.before(elementWrap);
        elementWrap.append(...this._flatElements);
        return elementWrap;
    }

    _unwrapElements()
    {
        const elementWrap = this._elementFirst.parentElement;
        elementWrap.after(...this._elements);
        elementWrap.remove();
        return elementWrap;
    }

    *[Symbol.iterator]() 
    {
        for(const key of Object.keys(this)) 
        {
            yield this[key];
        }
    }

    get _nextParentReferenceDOM()
    {
        return this.#parent?._nextParentReferenceDOM;
    }

    get _nextParentArrayDOM()
    {
        return this.#parent?._nextParentArrayDOM;
    }

    set _parent (parent) { this.#parent = parent; }

    get _element      () { return this.#elements[0];                       }
    get _elementFirst () { return this.#elements[0];                       }
    get _elementLast  () { return this.#elements[this.#elements.length-1]; }
    get _elements     () { return this.#elements;                          }
    get _hasElements  () { return this.#elements.length > 0;               }
    get _data         () { return this.#data;                              }
    get _parent       () { return this.#parent;                            }
    get _noRender     () { return this.#noRender;                          }
    get _templateDOM  () { return this.#templateDOM;                       }
};
xExtendClassForAttachments(XAccessibleDOM);

export function xGenerateDOM(specification)
{
    if(specification instanceof XObject)
    {
        const elementObject = document.createElement('ul');
        for(const key in specification._value)
        {
            const elementItem = document.createElement('li');
            const elementKey = document.createElement('b');
            const elementValue = xGenerateDOM(specification._value[key]);
            elementKey.innerText = key + ': ';
            elementItem.appendChild(elementKey);
            elementValue.setAttribute('data-x-data', key);
            elementItem.appendChild(elementValue);
            elementObject.appendChild(elementItem);
        }
        return elementObject;
    }
    else if(specification instanceof XArray)
    {
        const elementArray = document.createElement('ol');
        const elementItem = document.createElement('li');
        const elementValue = xGenerateDOM(specification._type);
        elementValue.setAttribute('data-x-data', '');
        elementItem.appendChild(elementValue);
        elementArray.appendChild(elementItem);
        return elementArray;
    }
    const elementPrimitive = document.createElement('span');
    elementPrimitive.innerHTML = String(specification.valueOf());
    return elementPrimitive;
}

export function xCreateReactiveDOM(element, data)
{
    data._addAttachment(element);
    if(data instanceof XObject)
    {
        const accessibleLayer = xGetNextElementLayer(element, true);
        for(const key in data._value)
        {
            if(key in accessibleLayer)
            {
                for(const elementChild of accessibleLayer[key])
                {
                    xCreateReactiveDOM(elementChild, data._value[key])
                }
            }
        }
        return;
    }
    else if(data instanceof XArray)
    {
        element._XDOM_template = element.firstElementChild.outerHTML;
        element._XTypes_push = (...values) => 
        {
            for(const value of values)
            {
                const dom = xTemplateToDOM(element._XDOM_template);
                const accessibleLayer = xGetNextElementLayer(dom);
                for(const elementChild of accessibleLayer._)
                {
                    xCreateReactiveDOM(elementChild, value);
                }
                element.appendChild(dom);
            }
        };
        element._XTypes_pop = () => 
        {
            element.lastElementChild.remove();
        };
        element._XTypes_setValue = (data) => 
        {
            element.innerHTML = '';
            for(const dataItem of data._value)
            {
                element._XTypes_push(dataItem);
            }
        };
        element._XTypes_setValue(data);
        return;
    }

    const tagName = element.tagName.toLowerCase();
    let setValueFunction = null;
    let setValue = function(setValue)
    {
        return (value) => {
            element._XTypes_setValue = () => {};
            setValue(value);
            element._XTypes_setValue = setValueFunction;
        };
    };
    if(data instanceof XBoolean)
    {
        setValue = setValue((value) => 
        {
            data._value = value;
        });
    }
    else if(data instanceof XInteger)
    {
        setValue = setValue((value) => 
        {
            data._value = parseInt(value);
        });
    }
    else if(data instanceof XNumber)
    {
        setValue = setValue((value) => 
        {
            data._value = parseFloat(value);
        });
    }
    else if(data instanceof XString)
    {
        setValue = setValue((value) => 
        {
            data._value = value.replaceAll('&nbsp;', ' ');
        });
    }
    else
    {
        return;
    }
    let onChange = null;
    if(tagName == 'input' || tagName == 'textarea')
    {
        setValueFunction = (data) => element.value = data._value;
        onChange = () => setValue(element.value);
    }
    else
    {
        element.contentEditable = true;
        setValueFunction = (data) => element.innerHTML = data._value;
        onChange = () => setValue(element.innerHTML);
    }
    element.addEventListener('input', onChange);
    element._XTypes_setValue = setValueFunction;
    element._XTypes_setValue(data);
}
const element = document.createElement('input');

/*
export function xCreateDOM(data)
{
    if(data instanceof XObject)
    {
        const elementObject = document.createElement('ul');
        for(const key in data._value)
        {
            const elementItem = document.createElement('li');
            const elementKey = document.createElement('b');
            const elementValue = xCreateDOM(data._value[key]);
            elementKey.innerText = key + ': ';
            xBindElementToReactiveData(elementValue, data._value[key]);
            elementItem.appendChild(elementKey);
            elementItem.appendChild(elementValue);
            elementObject.appendChild(elementItem);
        }
        return elementObject;
    }
    else if(data instanceof XArray)
    {
        const elementArray = document.createElement('ol');
        elementArray._XDOM_templateItem = '<li></li>';
        for(const key in data._value)
        {
            const elementItem = document.createElement('li');
            const elementValue = xCreateDOM(data._value[key]);
            xBindElementToReactiveData(elementValue, data._value[key]);
            elementItem.appendChild(elementValue);
            elementArray.appendChild(elementItem);
        }
        return elementArray;
    }
    else
    {
        return document.createElement('span');
    }
}
*/

export function xBindElementToReactiveData(element, data)
{
    data._addAttachment(element);
    element.setAttribute('data-x-types', '');
    element._XTypes_data = data;
    if(data instanceof XObject)
    {
        return;
    }
    else if(data instanceof XArray)
    {
        setValueFunction = (data) => {
            for(const elementXTypes of element.querySelectorAll('[data-x-types]'))
            {
                elementXTypes._XTypes_data._removeAttachment(elementXTypes);
            }
            element.innerHTML = '';
        };
        element._XTypes_setValue(data);
        return;
    }

    const tagName = element.tagName.toLowerCase();
    let setValueFunction = null;
    let setValue = function(setValue)
    {
        return (value) => {
            element._XTypes_setValue = () => {};
            setValue(value);
            element._XTypes_setValue = setValueFunction;
        };
    };
    if(data instanceof XBoolean)
    {
        setValue = setValue((value) => 
        {
            data._value = value;
        });
    }
    else if(data instanceof XInteger)
    {
        setValue = setValue((value) => 
        {
            data._value = parseInt(value);
        });
    }
    else if(data instanceof XNumber)
    {
        setValue = setValue((value) => 
        {
            data._value = parseFloat(value);
        });
    }
    else if(data instanceof XString)
    {
        setValue = setValue((value) => 
        {
            data._value = value.replaceAll('&nbsp;', ' ');
        });
    }
    else
    {
        return;
    }
    let onChange = null;
    if(tagName == 'input')
    {
        setValueFunction = (data) => element.value = data._value;
        onChange = () => setValue(element.value);
    }
    else
    {
        element.contentEditable = true;
        setValueFunction = (data) => element.innerHTML = data._value;
        onChange = () => setValue(element.innerHTML);
    }
    element.addEventListener('input', onChange);
    element._XTypes_setValue = setValueFunction;
    element._XTypes_setValue(data);
}

export function xAppendAccessibleDOM(mount, dom)
{
    if('child' in dom)
    {
        mount.appendChild(dom.child);
    }
    else if('children' in dom)
    {
        for(const child of dom.children)
        {
            mount.appendChild(child);
        }
    }
}

export function xCreateAccessibleDOM(template, settings)
{
    const _settings = {
        keepIDs: [],
        keepClasses: [],
    }
    const dom = new DOMParser().parseFromString(template, "text/html");
    const accessibleDOMElement = {};
    const accessibleDOMElements = {};
    for(const element of dom.body.querySelectorAll('[data-x-id]'))
    {
        const id = element.dataset['xId'];
        accessibleDOMElement[id] = element;
        if(!_settings.keepIDs.includes(id))
        {
            delete element.dataset['xId'];
        }
    }
    for(const element of dom.body.querySelectorAll('[data-x-class]'))
    {
        const classes = element.dataset['xClass'].split(' ');
        const keptClasses = [];
        for(const name of classes)
        {
            if(!(name in accessibleDOMElements))
            {
                accessibleDOMElements[name] = [];
            }
            accessibleDOMElements[name].push(element);
            if(_settings.keepClasses.includes(name))
            {
                keptClasses.push(name);
            }
        }
        if(keptClasses.length == 0)
        {
            delete element.dataset['xClass'];
        }
        else
        {
            element.dataset['xClass'] = keptClasses.join(' ');
        }
    }
    if('xKeep' in dom.body.dataset)
    {
        delete dom.body.dataset['xKeep'];
        return {
            child: dom.body,
            element: accessibleDOMElement,
            elements: accessibleDOMElements
        };
    }
    else if(dom.body.children.length == 1)
    {
        return {
            child: dom.body.children[0],
            element: accessibleDOMElement,
            elements: accessibleDOMElements
        };
    }
    const children = [];
    for(const child of dom.body.children)
    {
        children.push(child);
    }
    return {
        children: children,
        element: accessibleDOMElement,
        elements: accessibleDOMElements
    };
}

export function xQuerySelectorDataID(element, id)
{
    return element.querySelector('[data-x-id="' + id + '"]');
}

export function xQuerySelectorAllDataID(element, id)
{
    return element.querySelectorAll('[data-x-id="' + id + '"]');
}